import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Select, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { createMeeting, getAvailability } from '@/data/client';
import { useMembers } from '@/data/hooks';
import { useMe } from '@/auth/useMe';
import { ApiError } from '@/data/api';
import { FormError, Modal } from './Modal';
import { ConnectGoogleButton, useGoogleStatus } from './GoogleConnect';

const DURATIONS = [15, 30, 45, 60, 90, 120].map((m) => ({ value: String(m), label: m < 60 ? `${m} min` : `${m / 60} hr${m > 60 ? 's' : ''}` }));

export interface GuestOption { contactId: string; name: string; email: string | null }

/* Pick people → see when the team is free → pick a slot → Google sends the
   invites. Only colleagues' calendars are checked; guests outside Triton are
   invited but their availability is unknown. Times show in the workspace time
   zone, which is also the zone the event is created in. */
export function ScheduleMeetingModal({ companyId, dealId, guests, defaultTitle, onClose }: {
  companyId?: string; dealId?: string; guests: GuestOption[]; defaultTitle: string; onClose: () => void;
}) {
  const qc = useQueryClient();
  const { me } = useMe();
  const { members } = useMembers();
  const { data: google } = useGoogleStatus();
  const [title, setTitle] = useState(defaultTitle);
  const [agenda, setAgenda] = useState('');
  const [duration, setDuration] = useState(30);
  const [team, setTeam] = useState<string[]>([]);
  const [contactIds, setContactIds] = useState<string[]>(guests.filter((g) => g.email).slice(0, 1).map((g) => g.contactId));
  const [extra, setExtra] = useState<string[]>([]);
  const [draftEmail, setDraftEmail] = useState('');
  const [slot, setSlot] = useState<string | null>(null);

  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const avail = useQuery({
    queryKey: ['availability', [...team].sort(), duration],
    queryFn: () => getAvailability({ attendeeUserIds: team, durationMin: duration, days: 10 }),
    enabled: !!google?.connected,
    staleTime: 60_000,
  });
  const create = useMutation({
    mutationFn: () => createMeeting({
      title, agenda: agenda || undefined, startIso: slot!, durationMin: duration,
      attendeeUserIds: team, contactIds, guestEmails: extra, companyId, dealId,
    }),
    onSuccess: () => { for (const k of ['meetings', 'company', 'deal', 'contact']) void qc.invalidateQueries({ queryKey: [k] }); onClose(); },
  });

  const addEmail = () => {
    const e = draftEmail.trim().toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) && !extra.includes(e)) setExtra([...extra, e]);
    setDraftEmail('');
  };
  const tz = avail.data?.timezone ?? me?.workspace.timezone ?? 'America/Chicago';
  const byDay = useMemo(() => groupByDay(avail.data?.slots ?? [], tz), [avail.data, tz]);
  const unreadable = avail.data?.people.filter((p) => p.error) ?? [];
  const guestCount = team.length + contactIds.length + extra.length;
  const notConnected = google && !google.connected;
  const needsReconnect = avail.error instanceof ApiError && avail.error.code === 'google_not_connected';

  return (
    <Modal title="Schedule a meeting" wide onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={() => create.mutate()} disabled={!slot || !title.trim() || !guestCount || create.isPending} iconLeft={<Icon name="send" size={15} />}>
        {create.isPending ? 'Sending…' : 'Send invites'}
      </Button>
    </>}>
      {notConnected || needsReconnect ? (
        <div className="crm-form">
          <p>Meetings are created on your Google Calendar, and Google sends the invitations. Connect your Triton Google account first.</p>
          <div><ConnectGoogleButton /></div>
        </div>
      ) : (
        <div className="crm-form">
          <div className="crm-grid2">
            <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Select label="Length" options={DURATIONS} value={String(duration)} onChange={(e) => { setDuration(Number(e.target.value)); setSlot(null); }} />
          </div>

          <div className="crm-chips-field">
            <span className="tr-field__label">Team</span>
            <div className="crm-chips">
              {members.map((m) => {
                const self = m.userId === me?.userId;
                const on = self || team.includes(m.userId);
                return (
                  <button key={m.userId} type="button" className={`crm-chip ${on ? 'is-on' : ''}`} disabled={self}
                    onClick={() => { toggle(team, setTeam, m.userId); setSlot(null); }}>
                    {on && <Icon name="check" size={12} />}{m.name ?? m.email}{self ? ' (you, organizer)' : ''}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="crm-chips-field">
            <span className="tr-field__label">Guests</span>
            <div className="crm-chips">
              {guests.map((g) => (
                <button key={g.contactId} type="button" className={`crm-chip ${contactIds.includes(g.contactId) ? 'is-on' : ''}`} disabled={!g.email}
                  title={g.email ? g.email : 'No email address on file'} onClick={() => toggle(contactIds, setContactIds, g.contactId)}>
                  {contactIds.includes(g.contactId) && <Icon name="check" size={12} />}{g.name}{g.email ? '' : ' (no email)'}
                </button>
              ))}
              {extra.map((e) => (
                <button key={e} type="button" className="crm-chip is-on" onClick={() => setExtra(extra.filter((x) => x !== e))} title="Remove">
                  {e} <Icon name="x" size={12} />
                </button>
              ))}
              <input className="crm-chip-input" type="email" placeholder="+ add email" value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)} onBlur={addEmail}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(); } }} />
            </div>
          </div>

          <div className="crm-chips-field">
            <span className="tr-field__label">When <span className="crm-cell-sub">· times in {tz.replace('_', ' ')} · team calendars checked</span></span>
            {avail.isLoading && <p className="crm-cell-muted">Checking calendars…</p>}
            {unreadable.length > 0 && (
              <p className="crm-callout">Couldn't read {unreadable.map((p) => p.name ?? p.email).join(', ')}'s calendar — their busy times aren't included.</p>
            )}
            <div className="crm-slots">
              {byDay.map(([day, slots]) => (
                <div key={day} className="crm-slots__day">
                  <strong>{day}</strong>
                  {slots.map((s) => (
                    <button key={s.iso} type="button" className={`crm-slot ${slot === s.iso ? 'is-on' : ''}`} onClick={() => setSlot(s.iso)}>{s.label}</button>
                  ))}
                </div>
              ))}
              {avail.data && !byDay.length && <p className="crm-cell-muted">No shared free time in the next two weeks. Try fewer people or a shorter meeting.</p>}
            </div>
          </div>

          <Textarea label="Agenda (included in the invite)" rows={3} value={agenda} onChange={(e) => setAgenda(e.target.value)} />
          <FormError error={avail.error && !needsReconnect ? avail.error : create.error} />
        </div>
      )}
    </Modal>
  );
}

export function groupByDay(slots: string[], tz: string): [string, { iso: string; label: string }[]][] {
  const day = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' });
  const time = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
  const map = new Map<string, { iso: string; label: string }[]>();
  for (const iso of slots) {
    const d = day.format(new Date(iso));
    if (!map.has(d)) map.set(d, []);
    map.get(d)!.push({ iso, label: time.format(new Date(iso)) });
  }
  return [...map.entries()].slice(0, 7);
}
